import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';

import { isEmpty } from 'lodash';
import moment from 'moment';
import chroma from 'chroma-js';

import {
    Chart,
    ChartSeries,
    ChartSeriesItem,
    ChartXAxis,
    ChartXAxisItem,
    ChartYAxis,
    ChartYAxisItem,
    ChartTooltip,
} from '@progress/kendo-react-charts';

import BobLoadingMask from '../../../components/BobLoadingMask';

const TooltipRenderer = ({ point }) => {
    return (
        <React.Fragment>
            <div>
                Date: {point.value.x} {point.value.y}
            </div>
            <div>
                Energy Usage: {point.value.value}
            </div>
        </React.Fragment>
    );
};

const generateTimeArray = (interval) => {
    let times = [];
    let startTime = 0;
    let ap = ['AM', 'PM'];

    for (let i = 0; startTime < 24 * 60; i++) {
        let hh = Math.floor(startTime / 60);
        let mm = (startTime % 60);
        times[i] = ('0' + (hh % 12)).slice(-2) + ':' + ('0' + mm).slice(-2) + ' ' + ap[Math.floor(hh / 12)];
        times[i] = times[i].replace('00:', '12:');
        if (times[i].charAt(0) === '0') {
            times[i] = times[i].substring(1);
        }


        startTime = startTime + interval;
    }

    return times.reverse();
};

const HeatMap = ({
    bobLoading,
    data,
    hasInitialized,
    ...props
}) => {
    const [gridData, setGridData] = useState([]);
    const [scoreChartHeight, setScoreChartHeight] = useState('80vh');
    const minWidth2560 = matchMedia('(min-width: 2560px');
    const zoom125 = matchMedia('(min-resolution: 1.25dppx');
    const [max, setMax] = useState(0);
    const [min, setMin] = useState(0);
    const [target, setTarget] = useState(0);
    const [xCategories, setXCategories] = useState([]);
    const [yCategories, setYCategories] = useState([]);

    useEffect(() => {
        if (isEmpty(data)) {
            setGridData([]);
        } else {
            let xAxis = [];

            let formattedPayload = [];

            const payload = data.energyTimeSeries.currentTimeSeries;

            payload.forEach(item => {
                const date = moment(item.start).format('M/D/YYYY');
                const time = moment(item.start).format('h:mm A');
                xAxis.push(date);
                formattedPayload.push([date, time, item.energyUse]);              
            });

            xAxis = [...new Set(xAxis)];
            setXCategories(xAxis);
            setYCategories(generateTimeArray(15));

            const max = Math.max(...payload.map(item => item.energyUse));
            const min = Math.min(...payload.map(item => item.energyUse));

            setMax(max);
            setMin(min);
            setTarget(data.targetAverageValue);
            setGridData(formattedPayload);
        }
    }, [data]);

    const updateScoreChart2560 = (e) => {
        if (e.matches) {
            setScoreChartHeight('70vh');
        }
        else {
            setScoreChartHeight('80vh');
        }

    };

    const mqlZoom125 = (e) => {
        if (e.matches) {
            setScoreChartHeight('49vh');
        }
        else {
            setScoreChartHeight('60vh');
        }
    };

    useEffect(() => {
        return () => {
            minWidth2560.removeEventListener('change', updateScoreChart2560);
            zoom125.removeEventListener('change', mqlZoom125);
        };
    }, []);

    useEffect(() => {
        if (window.devicePixelRatio === 1) {
            setScoreChartHeight('60vh');
        }
        else {
            setScoreChartHeight('49vh');
        }

        minWidth2560.addEventListener('change', updateScoreChart2560);
        zoom125.addEventListener('change', mqlZoom125);

        const mediaQuery = window.matchMedia('(min-width: 2560px)');
        if (mediaQuery.matches) {
            updateScoreChart2560(mediaQuery);
        }

    }, []);

    const upperScale = chroma.scale(['#FF0000', '#610C04']);
    const lowerScale = chroma.scale(['lightgreen', 'orange']);

    const renderColor = e => {
        let scaleColor;

        if (e.value.value > target) {
            const range = max - target;
            scaleColor = upperScale((e.value.value - target) / range);
        } else {
            const range = target - min;
            scaleColor = lowerScale(e.value.value / range);
        }

        const colorArray = scaleColor._rgb;

        const rgbString = `rgb(${Math.round(colorArray[0])},${Math.round(colorArray[1])},${Math.round(colorArray[2])})`;
        const hex = '#' + rgbString.slice(4, -1).split(',').map(x => (+x).toString(16).padStart(2, 0)).join('');
        return hex;
    };

    return (
        <div>
            <div style={{ height: scoreChartHeight, overflow: 'auto' }}>
                <Chart
                    style={{
                        height: '150rem'
                    }}
                    pannable={true}
                    zoomable={true}
                    renderAs="canvas"
                >
                    <ChartTooltip render={TooltipRenderer} />
                    <ChartSeries>
                        <ChartSeriesItem
                            type="heatmap"
                            color={renderColor}
                            data={gridData}
                        />
                    </ChartSeries>
                    <ChartYAxis>
                        <ChartYAxisItem categories={yCategories} />
                    </ChartYAxis>
                    <ChartXAxis>
                        <ChartXAxisItem categories={xCategories} />
                    </ChartXAxis>
                </Chart>
                {
                    bobLoading.includes('FetchEquipmentEnergyData') || bobLoading.includes('FetchSiteEnergyData') || !hasInitialized ?
                        <BobLoadingMask />
                        :
                        null
                }
            </div>
        </div>
    );
};

const mapStateToProps = state => {
    return {
        bobLoading: state.bob.bobLoading
    };
};

export default connect(mapStateToProps, null)(HeatMap);
